import React, { useEffect, useMemo, useRef, useState } from "react";
import Page from "components/page/Page";
import { t } from "i18next";
import { useDispatch } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleUp } from "@fortawesome/pro-regular-svg-icons/faCircleUp";
import { StatusCode } from "api/protocols";
import {
  GetEmailCampaignAnalytics,
  GetEmailCampaignAnalyticsRecipients,
  GetEmailCampaignAnalyticsRecipientsExport,
  GetEmailCampaigns,
  IEmailCampaign,
  IEmailCampaignAnalytics,
  IEmailCampaignAnalyticsRecipient,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns";
import { PostEmailCampaignDraftPreview } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilder";
import type { IEmailCampaignDesignStatus } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import Card from "components/card/Card";
import Spin from "components/spin/spin";
import { ButtonNew } from "components/buttonNew";
import { showError } from "redux/actions/ui";
import { downloadCsv } from "helpers/Helpers";
import snakeCase from "lodash/snakeCase";
import { EmailBuilderPreviewFrame } from "pages/secure/facility/marketing/emailBuilder/shell/preview/EmailBuilderPreviewFrame";
import { EmailBuilderPreviewLoadingFrame } from "pages/secure/facility/marketing/emailBuilder/shell/preview/EmailBuilderPreviewLoadingFrame";
import { EMAIL_BUILDER_PREVIEW_CONTENT_WIDTH_PX } from "pages/secure/facility/marketing/emailBuilder/shell/preview/emailBuilderPreviewConstants";
import "pages/secure/facility/marketing/emailBuilder/shell/preview/emailBuilderPreview.scss";
import { TopLinksFilter } from "./TopLinksFilter";
import { EmailCampaignTopLinkPreviewCallout } from "./EmailCampaignTopLinkPreviewCallout";
import {
  getEmailCampaignTopLinkLabel,
  sortEmailCampaignTopLinks,
} from "./emailCampaignTopLinks";
import "./emailCampaignTopLinkPreview.scss";

const RECIPIENTS_PAGE_SIZE = 10;

function emailCampaignHasPreviewableDesign(design?: IEmailCampaignDesignStatus | null): boolean {
  if (design == null) {
    return false;
  }

  return design.has_draft || design.active_published_design_id != null;
}

function recipientName(recipient: IEmailCampaignAnalyticsRecipient): string {
  const name = [recipient.first_name, recipient.last_name].filter(Boolean).join(" ");
  return name || recipient.email;
}

export default function EmailCampaignTopLinkPreview() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{ selectedUrl?: string }>();
  const { id: campaignIdParam } = useParams<{ id: string }>();
  const campaignId = Number(campaignIdParam);
  const initialSelectedUrl = location.state?.selectedUrl ?? null;

  const [campaign, setCampaign] = useState<IEmailCampaign | null>(null);
  const [analytics, setAnalytics] = useState<IEmailCampaignAnalytics | null>(null);
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);
  const [selectedTopLinkUrl, setSelectedTopLinkUrl] = useState<string | null>(initialSelectedUrl);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [recipients, setRecipients] = useState<IEmailCampaignAnalyticsRecipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [recipientsOffset, setRecipientsOffset] = useState(0);
  const [recipientsTotal, setRecipientsTotal] = useState(0);

  const previewStageRef = useRef<HTMLDivElement | null>(null);

  const sortedTopLinks = useMemo(
    () => sortEmailCampaignTopLinks(analytics?.top_links ?? [], false),
    [analytics?.top_links],
  );

  const selectedLink = useMemo(
    () => sortedTopLinks.find(link => link.url === selectedTopLinkUrl),
    [selectedTopLinkUrl, sortedTopLinks],
  );

  const selectedLinkRank = useMemo(() => {
    if (selectedTopLinkUrl == null) {
      return 0;
    }

    const index = sortedTopLinks.findIndex(link => link.url === selectedTopLinkUrl);
    return index >= 0 ? index + 1 : 0;
  }, [selectedTopLinkUrl, sortedTopLinks]);

  useEffect(() => {
    async function loadCampaign() {
      const resp = await GetEmailCampaigns({ id: campaignIdParam }, false);
      if (resp.status !== StatusCode.OK || !Array.isArray(resp.data) || resp.data.length === 0) {
        dispatch(showError(t("Could not find email campaign")));
        history.push("/admin/email-marketing/campaigns");
        return;
      }

      setCampaign(resp.data[0]);
    }

    async function loadAnalytics() {
      const resp = await GetEmailCampaignAnalytics({ id: campaignId }, false);
      if (resp.status !== StatusCode.OK || !resp.data) {
        dispatch(showError(resp.message || t("Unable to load campaign analytics.")));
        setAnalyticsLoaded(true);
        return;
      }

      const topLinks = resp.data.top_links ?? [];
      setAnalytics({
        ...resp.data,
        top_links: topLinks,
      });
      setSelectedTopLinkUrl(current => {
        if (current != null && topLinks.some(link => link.url === current)) {
          return current;
        }

        if (initialSelectedUrl != null && topLinks.some(link => link.url === initialSelectedUrl)) {
          return initialSelectedUrl;
        }

        return topLinks[0]?.url ?? null;
      });
      setAnalyticsLoaded(true);
    }

    void loadCampaign();
    void loadAnalytics();
  }, [campaignId, campaignIdParam, dispatch, history, initialSelectedUrl]);

  useEffect(() => {
    if (campaign == null || !emailCampaignHasPreviewableDesign(campaign.design)) {
      setPreviewLoading(false);
      setPreviewHtml(null);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewHtml(null);

    void (async () => {
      const resp = await PostEmailCampaignDraftPreview(campaignId, false);
      if (cancelled) {
        return;
      }

      if (resp.status !== StatusCode.OK || resp.data?.data?.html == null) {
        setPreviewLoading(false);
        dispatch(showError(resp.message || t("Could not load email preview.")));
        return;
      }

      setPreviewHtml(resp.data.data.html);
      setPreviewLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [campaign, campaignId, dispatch]);

  useEffect(() => {
    if (analytics == null || selectedTopLinkUrl == null) {
      return;
    }

    let cancelled = false;

    async function loadRecipients() {
      setRecipientsLoading(true);
      const resp = await GetEmailCampaignAnalyticsRecipients(
        {
          id: campaignId,
          metric: "top_links",
          page: Math.floor(recipientsOffset / RECIPIENTS_PAGE_SIZE) + 1,
          url: selectedTopLinkUrl ?? undefined,
          send_batch_id: analytics?.send_batch_id ?? undefined,
          per_page: RECIPIENTS_PAGE_SIZE,
        },
        false,
      );

      if (cancelled) {
        return;
      }

      setRecipientsLoading(false);
      if (resp.status !== StatusCode.OK) {
        dispatch(showError(resp.message || t("Unable to load campaign recipients.")));
        return;
      }

      setRecipients(resp.data?.recipients?.data ?? []);
      setRecipientsTotal(resp.data?.recipients?.total ?? 0);
    }

    void loadRecipients();

    return () => {
      cancelled = true;
    };
  }, [analytics, campaignId, dispatch, recipientsOffset, selectedTopLinkUrl]);

  async function fetchExport() {
    const resp = await GetEmailCampaignAnalyticsRecipientsExport(
      {
        id: campaignId,
        metric: "top_links",
        url: selectedTopLinkUrl ?? undefined,
        send_batch_id: analytics?.send_batch_id ?? undefined,
      },
      true,
    );

    if (resp.status !== StatusCode.OK || typeof resp.data !== "string") {
      dispatch(showError(resp.message || t("Unable to export campaign recipients.")));
      return;
    }

    downloadCsv(resp.data, `${snakeCase(campaign?.name ?? "email_campaign")}_top_links_recipients.csv`);
  }

  function handleSelectLink(url: string) {
    setSelectedTopLinkUrl(url);
    setRecipientsOffset(0);
  }

  function handleRecipientsPage(direction: "prev" | "next") {
    setRecipientsOffset(current =>
      direction === "next"
        ? current + RECIPIENTS_PAGE_SIZE
        : Math.max(0, current - RECIPIENTS_PAGE_SIZE),
    );
  }

  if (!analyticsLoaded) {
    return (
      <Page
        title={t("Preview link")}
        subtitle={t("View where the link resides in the published email and recipients who clicked it.")}
        breadcrumbs={[
          { label: t("Email marketing"), url: "/admin/email-marketing" },
          { label: campaign?.name ?? t("Campaign"), url: `/admin/email-marketing/campaigns/${campaignIdParam}` },
          { label: t("Preview link") },
        ]}
      >
        <Card className="my-0">
          <Card.Section>
            <div className="flex justify-center py-10">
              <Spin />
            </div>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  if (sortedTopLinks.length === 0) {
    return (
      <Page
        title={t("Preview link")}
        subtitle={t("View where the link resides in the published email and recipients who clicked it.")}
        breadcrumbs={[
          { label: t("Email marketing"), url: "/admin/email-marketing" },
          { label: campaign?.name ?? t("Campaign"), url: `/admin/email-marketing/campaigns/${campaignIdParam}` },
          { label: t("Preview link") },
        ]}
      >
        <Card className="my-0">
          <Card.Section>
            <p className="m-0 text-sm text-gray-600">{t("No link click data is available for this campaign yet.")}</p>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  const selectedLabel = selectedLink ? getEmailCampaignTopLinkLabel(selectedLink) : t("Select link");
  const rankPrefix = selectedLinkRank > 0 ? `#${selectedLinkRank} ` : "";

  return (
    <Page
      title={t("Preview link")}
      subtitle={t("View where the link resides in the published email and recipients who clicked it.")}
      primaryAction={{
        content: (
          <div>
            <FontAwesomeIcon icon={faCircleUp} className="mr-2" />
            {t("Export List")}
          </div>
        ),
        action: fetchExport,
      }}
      breadcrumbs={[
        { label: t("Email marketing"), url: "/admin/email-marketing" },
        { label: campaign?.name ?? t("Campaign"), url: `/admin/email-marketing/campaigns/${campaignIdParam}` },
        { label: t("Preview link") },
      ]}
    >
      <div
        className="email-campaign-top-link-preview"
        style={
          {
            "--email-campaign-top-link-preview-min-width": `${EMAIL_BUILDER_PREVIEW_CONTENT_WIDTH_PX}px`,
          } as React.CSSProperties
        }
      >
        <div className="email-campaign-top-link-preview__panel">
          <Card className="my-0">
            <Card.Section>
              <div className="email-campaign-top-link-preview__link-header">
                <div className="min-w-0">
                  <h2 className="email-campaign-top-link-preview__link-title">
                    {rankPrefix}
                    {selectedLabel}
                  </h2>
                  {selectedLink ? (
                    <p className="email-campaign-top-link-preview__link-subtitle">
                      {selectedLinkRank === 1
                        ? t("Top most-clicked link with {{count}} total clicks.", {
                            count: selectedLink.count,
                          })
                        : t("#{{rank}} most-clicked link with {{count}} total clicks.", {
                            rank: selectedLinkRank,
                            count: selectedLink.count,
                          })}
                    </p>
                  ) : null}
                </div>
                <TopLinksFilter
                  links={sortedTopLinks}
                  selected={selectedLink}
                  onChange={handleSelectLink}
                />
              </div>
            </Card.Section>
          </Card>

          <div
            className="email-campaign-top-link-preview__frame-shell email-builder-preview email-builder-preview--desktop relative rounded-2xl border bg-primary-25"
          >
            {previewLoading ? (
              <div className="email-builder-preview-body">
                <EmailBuilderPreviewLoadingFrame
                  previewViewport="desktop"
                  contentWidthPx={EMAIL_BUILDER_PREVIEW_CONTENT_WIDTH_PX}
                />
              </div>
            ) : null}

            {!previewLoading && previewHtml == null ? (
              <div className="email-builder-preview-body">
                <div className="email-builder-preview-status">
                  {t("Design the content of your email to preview it here.")}
                </div>
              </div>
            ) : null}

            {!previewLoading && previewHtml != null && selectedLink ? (
              <div className="email-builder-preview-body">
                <div
                  className="email-campaign-top-link-preview__preview-stage"
                  ref={previewStageRef}
                >
                  <EmailBuilderPreviewFrame
                    previewHtml={previewHtml}
                    previewViewport="desktop"
                    contentWidthPx={EMAIL_BUILDER_PREVIEW_CONTENT_WIDTH_PX}
                    iframeTitle={t("Email preview")}
                  />
                  <EmailCampaignTopLinkPreviewCallout
                    containerRef={previewStageRef}
                    previewHtml={previewHtml}
                    targetUrl={selectedLink.url}
                    label={selectedLink.label ?? getEmailCampaignTopLinkLabel(selectedLink)}
                    clickCount={selectedLink.count}
                    rank={selectedLinkRank}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <Card className="my-0">
          <Card.Section>
            <h3 className="m-0 text-base leading-6 font-semibold text-gray-900">{t("View recipients")}</h3>
            <p className="mt-1 mb-4 text-sm leading-5 text-gray-600">
              {t("View all the recipients who clicked this link.")}
            </p>

            {recipientsLoading ? (
              <div className="flex justify-center py-8">
                <Spin />
              </div>
            ) : recipients.length === 0 ? (
              <p className="m-0 text-sm text-gray-600">{t("No recipients found.")}</p>
            ) : (
              <div className="email-campaign-top-link-preview__recipients">
                {recipients.map(recipient => (
                  <div key={recipient.id} className="email-campaign-top-link-preview__recipient">
                    <div className="email-campaign-top-link-preview__recipient-name">
                      {recipientName(recipient)}
                    </div>
                    <div className="email-campaign-top-link-preview__recipient-email">{recipient.email}</div>
                  </div>
                ))}
              </div>
            )}

            {!recipientsLoading && recipientsTotal > RECIPIENTS_PAGE_SIZE ? (
              <div className="email-campaign-top-link-preview__pagination">
                <ButtonNew
                  type="default"
                  size="small"
                  disabled={recipientsOffset === 0}
                  onClick={() => handleRecipientsPage("prev")}
                >
                  {t("Previous")}
                </ButtonNew>
                <span className="text-sm text-gray-600">
                  {t("{{start}}-{{end}} of {{total}}", {
                    start: recipientsOffset + 1,
                    end: Math.min(recipientsOffset + RECIPIENTS_PAGE_SIZE, recipientsTotal),
                    total: recipientsTotal,
                  })}
                </span>
                <ButtonNew
                  type="default"
                  size="small"
                  disabled={recipientsOffset + RECIPIENTS_PAGE_SIZE >= recipientsTotal}
                  onClick={() => handleRecipientsPage("next")}
                >
                  {t("Next")}
                </ButtonNew>
              </div>
            ) : null}
          </Card.Section>
        </Card>
      </div>
    </Page>
  );
}
